import { Injectable, inject } from '@angular/core';
import { Classifier } from '@nano/common/classifier';
import { Currency } from '@nano/common/finance';
import { ApiEndpoints, ApiService, httpErrorToApiError } from '@nano/core/api';
import { AuthService, UserProfile, isPartySuspended } from '@nano/core/auth';
import { CONFIG } from '@nano/core/config';
import { ErrorService } from '@nano/core/error';
import { L10nService } from '@nano/core/l10n';
import { SseService } from '@nano/core/sse';
import { BehaviorSubject, Observable, Subject, combineLatest, iif, merge, of, race } from 'rxjs';
import { catchError, debounceTime, filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { Account, AccountsResponse, DEMO_ACCOUNT, hasDebitAvailableFunds, isDemoAccount, PermittedOperationCurrencyType } from './account';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable({ providedIn: 'root' })
export class AccountService {
  readonly accounts$: Observable<Account[]>;
  readonly hasAccount$: Observable<boolean>;
  readonly hasRealAccount$: Observable<boolean>;
  readonly hasBlockedAccount$: Observable<boolean>;
  readonly hasDebitAvailableFunds$: Observable<boolean>;
  readonly outgoingPaymentsAvailable$: Observable<boolean>;
  readonly pendingPaymentsAvailable$: Observable<boolean>;
  readonly selectedPartyHasAccounts$: Observable<boolean>;

  private hasAccountSubject: BehaviorSubject<boolean>;
  private selectedPartyHasAccountsSubject: BehaviorSubject<boolean>;
  private hasBlockedAccountSubject: BehaviorSubject<boolean>;
  private hasDebitAvailableFundsSubject: BehaviorSubject<boolean>;

  readonly accountNameUpdated$: Subject<boolean> = new Subject<boolean>();

  private readonly hasAccountStorageItemKey = 'ha_';

  private config = inject(CONFIG);

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private errorService: ErrorService,
    private sseService: SseService,
    private l10nService: L10nService
  ) {
    this.hasAccountSubject = new BehaviorSubject(this.hasAccount);
    this.hasAccount$ = this.hasAccountSubject.asObservable();

    this.hasBlockedAccountSubject = new BehaviorSubject(false);
    this.hasBlockedAccount$ = this.hasBlockedAccountSubject.asObservable();

    this.hasDebitAvailableFundsSubject = new BehaviorSubject(false);
    this.hasDebitAvailableFunds$ = this.hasDebitAvailableFundsSubject.asObservable();

    this.selectedPartyHasAccountsSubject = new BehaviorSubject(false);
    this.selectedPartyHasAccounts$ = this.selectedPartyHasAccountsSubject.asObservable();

    this.pendingPaymentsAvailable$ = combineLatest([this.hasAccount$, this.hasDebitAvailableFunds$, this.authService.userProfile$]).pipe(
      map(
        ([hasAccount, hasDebitAvailableFunds, userProfile]) =>
          ((this.config.paymentWaitForFundsEnabled && hasAccount) || hasDebitAvailableFunds) && !isPartySuspended(userProfile)
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.outgoingPaymentsAvailable$ = this.pendingPaymentsAvailable$.pipe(
      switchMap((pendingPaymentsAvailable) =>
        this.authService.userProfile$.pipe(map((userProfile) => pendingPaymentsAvailable && !!userProfile?.canCreatePayments && !isPartySuspended(userProfile)))
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.accounts$ = merge(
      this.authService.userProfile$,
      this.l10nService.languageChanged$,
      this.sseService.cardUpdated$,
      this.sseService.partyUpdated$,
      this.accountNameUpdated$,
      this.sseService.ibProductUpdated$,
      race(this.sseService.paymentEntryUpdated$, this.sseService.partyAvailableBalanceUpdated$.pipe(debounceTime(300)))
    ).pipe(
      switchMap((userProfile: UserProfile) =>
        iif(
          () => !userProfile,
          of([] as Account[]),
          this.apiService.get<AccountsResponse>(ApiEndpoints.accounts()).pipe(
            map((accounts) => this.mapAccounts(accounts)),
            catchError((error: unknown) => {
              this.errorService.handleRequestError(error);
              return of(
                this.mapAccounts({
                  selectedPartyHasAccounts: false,
                  newCardAllowed: false,
                  results: []
                } as AccountsResponse)
              );
            })
          )
        )
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.hasRealAccount$ = this.accounts$.pipe(
      filter((accounts) => accounts.length > 0),
      map((accounts) => !isDemoAccount(accounts[0]))
    );
  }

  openAccount(formSubmitId: string, currency = Currency.EUR): Observable<string> {
    const request: { currency: string; description?: string } = { currency, description: `${this.authService.userProfile?.fullName} ${currency} account` };
    return this.apiService.post<{ accountNumber: string }>(ApiEndpoints.accounts(), request, null, null, formSubmitId).pipe(
      map((openAccountResponse) => openAccountResponse.accountNumber),
      tap({
        next: () => this.hasAccountSubject.next(true)
      }),
      catchError((error: unknown) => {
        const apiError = httpErrorToApiError(error as HttpErrorResponse);
        this.errorService.showErrorModal(apiError);
        return of();
      })
    );
  }

  private mapAccounts(accountsResponse: AccountsResponse): Account[] {
    const accounts: Account[] = [];
    const name = this.authService.userProfile?.fullName;
    this.selectedPartyHasAccountsSubject.next(accountsResponse.selectedPartyHasAccounts);
    accountsResponse.results.forEach((account) => {
      const currencies = Object.keys(account.balances);
      currencies.forEach((currency) => {
        const availableFunds: number = account.availableFunds[currency] as number;
        const permittedOperationCurrencies = account.permittedOperationCurrencies
          ? new Map<PermittedOperationCurrencyType, Classifier[]>(
              Object.entries(account.permittedOperationCurrencies)
                .filter(([key]) => Object.values(PermittedOperationCurrencyType).includes(key as PermittedOperationCurrencyType))
                .map(([key, value]) => [key as PermittedOperationCurrencyType, value] as [PermittedOperationCurrencyType, Classifier[]])
            )
          : undefined;
        accounts.push({
          accountName: account.description || `${name} ${currency}`,
          description: account.description,
          availableFunds: availableFunds,
          branchBic: accountsResponse.branchBic,
          hasDebitAvailableFunds: hasDebitAvailableFunds(availableFunds, account.operationsBlock),
          reserved: account.holds ? (account.holds[currency] as number) : 0,
          currency: currency,
          card: account.lastOrderedCard,
          iban: account.number,
          isMain: account.isMain,
          isViewOnly: account.isViewOnly,
          operationsBlock: account.operationsBlock,
          newCardAllowed: accountsResponse.newCardAllowed,
          hasPlaisBlock: account.fundsBlocks?.length > 0,
          monthlyAvailableSpendingAmount: account.monthlyAvailableSpendingAmount
            ? account.monthlyAvailableSpendingAmount.value.toString() + ' ' + account.monthlyAvailableSpendingAmount.currency
            : null,
          permittedOperationCurrencies,
          paymentGatewayEligiblePayerCurrencies: account.paymentGatewayEligiblePayerCurrencies ?? []
        });
      });
    });
    const hasAccount: boolean = accounts.length > 0;
    if (!hasAccount) {
      accounts.push(DEMO_ACCOUNT);
    }
    this.hasAccount = hasAccount;
    this.hasAccountSubject.next(hasAccount);
    this.hasDebitAvailableFundsSubject.next(accounts.map((account) => account.hasDebitAvailableFunds).includes(true));
    this.hasBlockedAccountSubject.next(accounts.findIndex((account) => account.operationsBlock) > -1);
    return accounts;
  }

  private set hasAccount(hasAccount: boolean) {
    sessionStorage.setItem(this.hasAccountStorageItemKey, String(hasAccount));
  }

  private get hasAccount(): boolean {
    return Boolean(sessionStorage.getItem(this.hasAccountStorageItemKey) || false);
  }
}
